import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { BRAND } from "@/constants/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      if (user.role === "super_admin") navigate("/super-admin");
      else if (user.role === "admin") navigate("/admin");
      else if (user.role === "vendor") navigate("/vendor");
      else navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-[#04301b] via-[#0a4a27] to-[#1b6b24] text-white p-12">
        <Link to="/"><img src={BRAND.logo} alt="Zilkart" className="h-12 bg-white rounded-xl px-3 py-1.5 shadow-lg" /></Link>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight">Welcome back to Zilkart</h2>
          <p className="mt-4 text-white/80 max-w-sm">Sign in to track orders, manage your wishlist and checkout faster.</p>
        </div>
        <p className="text-sm text-white/60">© {new Date().getFullYear()} {BRAND.name} Technologies</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden inline-block mb-8"><img src={BRAND.logo} alt="Zilkart" className="h-9" /></Link>
          <h1 className="font-display text-3xl font-bold text-deep">Sign In</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Enter your credentials to continue.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          <p className="text-sm text-center mt-6 text-muted-foreground">
            New to Zilkart?{" "}
            <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="login-to-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
